// „Was ist neu" — erscheint einmal beim ersten Anmelden nach einem Update.
//
// Der Inhalt kommt vom Anbieter (Owner-Bereich → Neuigkeiten). Schließen
// merkt sich der Server: auf dem nächsten Gerät erscheint es nicht noch einmal.
// Aus der Mail (?neuigkeiten=1) zeigt es die neueste auch dann, wenn sie schon
// gesehen wurde — wer auf „Jetzt ansehen" klickt, will sie sehen.
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { api } from '../../../gemeinsam/lib/api';
import { datum } from '../../../gemeinsam/lib/daten';
import { useSprache } from '../../../gemeinsam/lib/i18n';
import { Fenster } from '../../../gemeinsam/komponenten/Fenster';
import { Knopf } from '../../../gemeinsam/komponenten/Basis';
import Icon from '../../../gemeinsam/komponenten/Icon';

type Neuigkeit = {
  id: string; version: string; titel: string; einleitung: string;
  punkte: Array<{ titel: string; text: string }>; veroeffentlichtAm: string;
};
type Fortschritt = { dabeiSeit: string | null; updates: number; verbesserungen: number };

export default function WasIstNeu() {
  const { t } = useSprache();
  const ort = useLocation();
  const geh = useNavigate();
  const [liste, setListe] = useState<Neuigkeit[]>([]);
  const [stand, setStand] = useState<Fortschritt | null>(null);
  const ausMail = new URLSearchParams(ort.search).get('neuigkeiten') === '1';

  useEffect(() => {
    let aktiv = true;
    api<{ neuigkeiten: Neuigkeit[]; fortschritt: Fortschritt }>('/neuigkeiten/ungesehen')
      .then(async d => {
        let neu = d.neuigkeiten;
        if (!neu.length && ausMail) neu = (await api<Neuigkeit[]>('/neuigkeiten')).slice(0, 1);
        if (aktiv) { setListe(neu); setStand(d.fortschritt); }
      })
      .catch(() => { /* kein Fenster ist besser als ein Fehler beim Anmelden */ });
    return () => { aktiv = false; };
  }, [ausMail]);

  if (!liste.length) return null;

  function schliessen() {
    setListe([]);
    void api('/neuigkeiten/gesehen', { methode: 'POST' }).catch(() => {});
    if (ausMail) geh({ search: '' }, { replace: true });
  }

  const [erste, ...weitere] = liste;
  return (
    <Fenster beiSchliessen={schliessen} breite="sm:max-w-xl"
             titel={<span className="flex items-center gap-2"><Icon name="stern" groesse={20} />{t('neuigkeiten.titel')}</span>}
             unterzeile={`LagerPro ${erste.version} · ${datum(erste.veroeffentlichtAm)}`}>
      <div className="space-y-5 p-5">
        <div>
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">{erste.titel}</h3>
          {erste.einleitung && <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">{erste.einleitung}</p>}
        </div>
        <ul className="space-y-3">
          {erste.punkte.map((p, i) => (
            <li key={i} className="flex gap-3">
              <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-marke-100 text-marke-700 dark:bg-marke-950 dark:text-marke-300">
                <Icon name="haken" groesse={13} />
              </span>
              <span>
                <span className="block text-sm font-medium text-slate-900 dark:text-slate-100">{p.titel}</span>
                {p.text && <span className="block text-sm text-slate-600 dark:text-slate-400">{p.text}</span>}
              </span>
            </li>
          ))}
        </ul>

        {weitere.length > 0 && (
          <div className="rounded-lg border border-slate-200 p-3 text-sm dark:border-slate-800">
            <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">{t('neuigkeiten.auchNeu')}</p>
            {weitere.map(w => (
              <p key={w.id} className="mt-1 text-slate-700 dark:text-slate-300">
                <span className="font-medium">{w.version}</span> · {w.titel}
              </p>
            ))}
          </div>
        )}

        {stand && stand.updates > 1 && (
          <div className="rounded-xl bg-gradient-to-r from-marke-50 to-white p-4 ring-1 ring-marke-100 dark:from-marke-950/40 dark:to-slate-900 dark:ring-marke-900">
            <p className="text-xs font-medium uppercase tracking-wide text-marke-700 dark:text-marke-300">{t('neuigkeiten.fortschritt')}</p>
            <p className="mt-1 text-sm text-slate-700 dark:text-slate-300">
              <strong className="text-2xl text-marke-700 dark:text-marke-300">{stand.updates}</strong> {t('neuigkeiten.updates')}
              {' · '}
              <strong className="text-2xl text-marke-700 dark:text-marke-300">{stand.verbesserungen}</strong> {t('neuigkeiten.verbesserungen')}
              {stand.dabeiSeit && <span className="text-slate-500 dark:text-slate-400"> {t('neuigkeiten.seit').replace('{datum}', datum(stand.dabeiSeit))}</span>}
            </p>
          </div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 pt-4 dark:border-slate-800">
          <p className="text-xs text-slate-500 dark:text-slate-400">{t('neuigkeiten.mailHinweis')}</p>
          <Knopf variante="primaer" onClick={schliessen}>{t('neuigkeiten.verstanden')}</Knopf>
        </div>
      </div>
    </Fenster>
  );
}
